/**
 * Creator page `/c/<handle>` (docs/DASHBOARD_PARITY.md §3.3): the OutlierOS creator workbench replica.
 * Header (action row + profile card + stats strip) shared by two tabs: **Reels** (default; presets, Transcribe all,
 * Export, search/sort/threshold, storage strip, 9:16 grid, inspector) and **Report** (KPIs and charts).
 *
 * Live after a scan (decision 75): one request loads the page (`/api/creators/by-handle/:handle/report`), the header
 * shows from the cached library card while it loads, and the page polls the sync status every 3 s while the scan's
 * reels, verifying reels or videos are still being written (or the scan ended under 3 minutes ago), reloading the
 * report only when it changed; filters, the open reel and bookmarks stay as they are.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ArrowLeft, BarChart3, Check, ExternalLink, Film, Filter, Flame, Grid3x3, Link2, Loader2, Mic, Search, Share2, Sparkles, Zap } from 'lucide-react';
import {
  DEFAULT_WINDOW, engagement, medianOwnEngagement, parseWindow, reelCounts, REPORT_DAY_CHOICES, REPORT_LATEST_CHOICES, windowKey, windowLabel,
  type ReportKpis, type ReportReel, type ReportWindow,
} from '@vw/core/src/report';
import { keepPolling, SYNC_POLL_MS, syncLine, type SyncStatus } from '@vw/core/src/sync';
import { Button, EmptyState, Input, Segmented, Select, Spinner, cn, formatWiseScore, toast, wiseScoreLabel, type TierThresholds } from '@vw/ui';
import { api, ApiError, type CreatorCard } from '../api';
import { CreatorAvatar } from '../components/CreatorAvatar';
import { CreatorBio } from '../components/CreatorBio';
import { NicheChips } from '../components/NicheFilter';
import { AddToCollection } from '../components/AddToCollection';
import { ReelCard } from '../components/workbench/ReelCard';
import { ReelInspector } from '../components/workbench/ReelInspector';
import { BatchProgress, TranscribeAllDialog, useBatchRunner } from '../components/workbench/TranscribeAll';
import { ExportMenu, fetchTranscripts, FORMAT_LABEL, runExport, type ExportFormat } from '../components/workbench/ExportMenu';
import { ReportTab } from '../components/workbench/ReportTab';
import { ago, compact, igReelsTab } from '../format';
import { Link, navigate, useLocation } from '../router';
import { useSession } from '../session';

interface ReportResponse {
  creator: CreatorCard;
  saved: boolean;
  window: string;
  outlierThreshold: number;
  tiers: TierThresholds;
  secondsPerCredit: number;
  kpis: ReportKpis;
  reels: ReportReel[];
  bookmarkedPks: string[];
  exportRowLimit: number | null;
  videoRetentionDays: number;
  /** Decision 81: videos saved at all (absent = an older API, which saved them). */
  saveVideos?: boolean;
  /** Decision 71: the score minimum ("Not enough reels yet (n/25)") and reels still waiting for corroboration. */
  minSample: number;
  /** `scores.window`: 0 = all qualifying reels (decision 83). Older API responses lack it. */
  scoreWindow?: number;
  awaitingVerification: number;
  /** Decision 75: this org's live sync status (its latest scan, verifying reels, videos still saving). */
  sync?: SyncStatus;
}

/** The profile header card (B): also shown from the cached library card while the report loads (decision 75). */
function ProfileHeader({ c, line, stats }: { c: CreatorCard; line: ReactNode; stats: ReactNode }) {
  return (
    <section className="flex flex-col gap-6 rounded-3xl border border-border bg-card p-6 shadow-sm sm:p-8 md:flex-row md:items-center md:justify-between">
      <div className="flex min-w-0 items-start gap-4">
        <CreatorAvatar url={c.avatarUrl} name={c.handle ?? ''} fullName={c.fullName} size={72} ring rounded="2xl" />
        <div className="min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-black sm:text-2xl">@{c.handle}</h1>
            {c.isVerified && <span className="text-primary" title="Verified">✓</span>}
            {c.category && <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{c.category}</span>}
          </div>
          <NicheChips niches={c.niches} />
          <div className="text-sm text-muted-foreground">{[c.fullName, `${compact(c.followers)} followers`].filter(Boolean).join(' · ')}</div>
          {c.bio && <CreatorBio bio={c.bio} />}
          {(c.bioLinks.length > 0 || c.externalUrl) && (
            <div className="flex flex-wrap gap-1.5">
              {(c.bioLinks.length ? c.bioLinks : [{ url: c.externalUrl!, title: null }]).map((l) => (
                <a key={l.url} href={l.url} target="_blank" rel="noreferrer nofollow" className="rounded-full border border-border px-2.5 py-0.5 text-xs text-primary hover:bg-secondary">{l.title || l.url.replace(/^https?:\/\/(www\.)?/, '').slice(0, 40)}</a>
              ))}
            </div>
          )}
          <div className="text-xs text-muted-foreground">{line}</div>
        </div>
      </div>
      {stats}
    </section>
  );
}

/** Decision 91: the Wise Score reason on a collab-only profile's page (existing text slots only). */
export const stubScoreNote = (handle: string) => `No score yet: open @${handle}'s Reels tab with the extension to analyse fully`;
/** Decision 91: the header's status line on a collab-only profile's page. */
export const collabProfileNote = (handle: string) => `Collab profile: open @${handle}'s Reels tab with the extension to analyse fully`;

type Preset = 'all' | 'top20' | 'mega' | 'engagement';
type Sort = 'outlier' | 'views' | 'likes' | 'comments' | 'engagement' | 'virality' | 'newest';
const SORTS: Array<{ value: Sort; label: string }> = [
  { value: 'outlier', label: '⚡ Wise Score' }, { value: 'views', label: '▶ Views' }, { value: 'likes', label: '❤️ Likes' },
  { value: 'comments', label: '💬 Comments' }, { value: 'engagement', label: '📈 Engagement' }, { value: 'virality', label: '📣 Virality' },
  { value: 'newest', label: '📅 Newest' },
];
const PAGE = 60;

export function Creator({ handle }: { handle: string }) {
  const { query } = useLocation();
  const { reloadLibrary, library } = useSession();
  // The scan the extension opened this page for (decision 75): its sync status, not just this org's latest scan.
  const scanParam = query.get('scan');
  const scanQ = scanParam && /^[0-9a-f-]{36}$/.test(scanParam) ? `&scan=${scanParam}` : '';
  const win: ReportWindow = parseWindow(query.get('window') ?? (query.get('limit') ? `latest:${query.get('limit')}` : null)) ?? DEFAULT_WINDOW;
  const wKey = windowKey(win);
  const tab = query.get('tab') === 'report' ? 'report' : 'reels';
  const [data, setData] = useState<ReportResponse | null>(null);
  const [error, setError] = useState<{ notFound: boolean; message: string } | null>(null);
  const [preset, setPreset] = useState<Preset>('all');
  const [sort, setSort] = useState<Sort>('outlier');
  const [q, setQ] = useState('');
  const [minMult, setMinMult] = useState(0);
  const [shown, setShown] = useState(PAGE);
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [marks, setMarks] = useState<Set<string>>(new Set());
  const [txDialog, setTxDialog] = useState<{ exportFormat: ExportFormat | null } | null>(null);
  const [share, setShare] = useState<{ url: string | null; busy: boolean } | null>(null);
  const [sync, setSync] = useState<SyncStatus | null>(null);
  const pollStart = useRef(Date.now());
  /** The reel open in the inspector, so a live reload keeps it open even when the list order changes. */
  const openPk = useRef<string | null>(null);

  const setParam = (k: string, v: string | null) => {
    const p = new URLSearchParams(location.search);
    if (v === null) p.delete(k); else p.set(k, v);
    navigate(`/c/${handle}${p.toString() ? `?${p}` : ''}`, { replace: true });
  };

  useEffect(() => {
    let live = true;
    setError(null);
    (async () => {
      try {
        // One request (decision 75): the handle lookup and the report together.
        const r = await api<ReportResponse>(`/api/creators/by-handle/${encodeURIComponent(handle)}/report?window=${wKey}${scanQ}`);
        if (!live) return;
        setData(r);
        setMarks(new Set(r.bookmarkedPks));
        pollStart.current = Date.now();
      } catch (e) {
        if (live) setError({ notFound: e instanceof ApiError && e.status === 404, message: (e as Error).message });
      }
    })();
    return () => { live = false; };
  }, [handle, wKey]);
  useEffect(() => { setShown(PAGE); setSelected(new Set()); }, [preset, sort, q, minMult, wKey]);
  useEffect(() => setSync(data?.sync ?? null), [data]);

  // Live sync (decision 75): poll the small status while something is pending; reload the report only when it changed.
  useEffect(() => {
    const igPk = data?.creator.igPk;
    if (!igPk || !sync || !keepPolling(sync, Date.now(), pollStart.current)) return;
    let live = true;
    const t = setTimeout(async () => {
      // A hidden tab doesn't poll (it looks again on the next tick once visible, within SYNC_MAX_POLL_MS).
      if (document.hidden) { setSync({ ...sync }); return; }
      try {
        const s = await api<SyncStatus>(`/api/creators/${igPk}/sync?${scanQ.slice(1)}`);
        if (!live) return;
        if (s.version === sync.version) { setSync(s); return; }
        const r = await api<ReportResponse>(`/api/creators/${igPk}/report?window=${wKey}${scanQ}`);
        // Rows replaced in place: filters, sort, search, the selection and local bookmark toggles stay as they are.
        if (live) setData(r);
      } catch {
        if (live) setSync({ ...sync }); // try again on the next tick (bounded by SYNC_MAX_POLL_MS)
      }
    }, SYNC_POLL_MS);
    return () => { live = false; clearTimeout(t); };
  }, [sync, data?.creator.igPk, wKey, scanQ]);
  useEffect(() => setOpenIdx(null), [tab]);

  // A reel counts as transcribed only once /api/transcripts returns it (that also brings its hook for the card).
  const batch = useBatchRunner((pks) => {
    void fetchTranscripts(pks).then((t) => {
      if (!Object.keys(t).length) return;
      setData((d) => d && ({ ...d, reels: d.reels.map((x) => (t[x.pk] ? { ...x, hasTranscript: true, hook: t[x.pk]!.hook ?? x.hook } : x)) }));
    }, () => { /* hooks show after the next load */ });
  });

  const visible = useMemo(() => {
    if (!data) return [];
    // Own reels with visible likes only (decision 71): hidden likes are not "0 engagement", and collabs aren't theirs.
    const engMed = medianOwnEngagement(data.reels) ?? 0;
    let list = data.reels;
    if (preset === 'mega') list = list.filter((r) => (r.outlierScore ?? 0) >= data.tiers.viral);
    if (preset === 'engagement') list = list.filter((r) => { const e = engagement(r); return e !== null && engMed > 0 && e >= 2 * engMed; });
    if (minMult > 0) list = list.filter((r) => (r.outlierScore ?? 0) >= minMult);
    const s = q.trim().toLowerCase();
    if (s) list = list.filter((r) => (r.caption ?? '').toLowerCase().includes(s) || (r.hook ?? '').toLowerCase().includes(s) || r.shortcode.toLowerCase().includes(s));
    const key = (r: ReportReel): number | null => sort === 'outlier' ? r.outlierScore : sort === 'views' ? r.views : sort === 'likes' ? (r.likesHidden ? null : r.likes)
      : sort === 'comments' ? r.comments : sort === 'engagement' ? engagement(r) : sort === 'virality' ? r.viralityScore : r.postedAt;
    if (preset === 'top20') return list.filter((r) => r.outlierScore !== null).sort((a, b) => b.outlierScore! - a.outlierScore!).slice(0, 20);
    return [...list].sort((a, b) => {
      const ka = key(a);
      const kb = key(b);
      return ka === null ? (kb === null ? 0 : 1) : kb === null ? -1 : kb - ka;
    });
  }, [data, preset, minMult, q, sort]);

  // Keep the open reel open across live reloads (its index may move).
  const openList = tab === 'report' ? data?.reels ?? [] : visible;
  useEffect(() => { openPk.current = openIdx !== null ? openList[openIdx]?.pk ?? null : null; }, [openIdx]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (openPk.current === null || openIdx === null) return;
    const i = openList.findIndex((r) => r.pk === openPk.current);
    if (i >= 0 && i !== openIdx) setOpenIdx(i);
  }, [openList]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return error.notFound ? (
      <EmptyState icon={<Film className="h-7 w-7" />} title={`@${handle} isn't in your library yet`}
        actions={<a className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-3.5 py-2 text-xs font-semibold text-primary-foreground" href={igReelsTab(handle)} target="_blank" rel="noreferrer">Open @{handle} on Instagram<ExternalLink className="h-3.5 w-3.5" /></a>}>
        Open their Reels tab on Instagram and press <b>Scan</b> in the extension's side panel. The analysis fills in here when the scan is done.
      </EmptyState>
    ) : <p className="text-sm text-rose-500">{error.message}</p>;
  }
  if (!data) {
    // The cached library card shows the header at once (decision 75); the rest loads below it.
    const cached = library?.find((x): x is Exclude<typeof x, { unavailable: true }> => !x.unavailable && x.handle?.toLowerCase() === handle.toLowerCase());
    if (!cached) return <Spinner label={`Loading creator intelligence for @${handle}…`} />;
    return (
      <div className="mx-auto max-w-7xl space-y-6 pb-16">
        <ProfileHeader c={cached} line={`Last scanned ${ago(cached.lastScannedAt)}`} stats={null} />
        <Spinner label={`Loading creator intelligence for @${handle}…`} />
      </div>
    );
  }

  const c = data.creator;
  // Their own reels: a collab is scored against their usual views (decision 71) but isn't their peak.
  const peak = data.reels.reduce((m, r) => (r.isCollab ? m : Math.max(m, r.outlierScore ?? 0)), 0);
  const n = reelCounts(data.reels);
  const usual = c.scoreBasis === 'likes' ? 'Usual likes' : 'Usual views';
  // Decision 91: a collab-only profile (stub) has no baseline until its own Reels tab is scanned (decision 71).
  const stubNote = c.isStub ? stubScoreNote(c.handle ?? handle) : undefined;
  const notEnough = stubNote ?? `Not enough reels yet (${Math.min(c.baselineN, data.minSample)}/${data.minSample})`;
  const saved = data.reels.filter((r) => r.videoUrl && r.videoSavedAt).length; // our copies, not temporary Instagram links (decision 74)
  const expiring = data.reels.filter((r) => r.videoUrl && r.videoSavedAt && r.videoSavedAt + data.videoRetentionDays * 86_400_000 - Date.now() < 3 * 86_400_000).length;
  const counts: Partial<Record<Preset, number>> = {
    all: data.reels.length, top20: Math.min(20, data.reels.filter((r) => r.outlierScore !== null).length),
    mega: data.reels.filter((r) => (r.outlierScore ?? 0) >= data.tiers.viral).length,
  };
  const toggleSel = (pk: string) => { const n = new Set(selected); n.has(pk) ? n.delete(pk) : n.add(pk); setSelected(n); };

  /** Exports what is in view. `rows` lets "Transcribe & export" pass the rows as they are after transcribing. */
  async function doExport(format: ExportFormat, withTranscripts: boolean, rows: ReportReel[] = visible, alsoTranscribed: string[] = []): Promise<boolean> {
    try {
      await runExport(format, rows, { handle: c.handle ?? handle, fullName: c.fullName, tiers: data!.tiers, limit: data!.exportRowLimit, withTranscripts, alsoTranscribed });
      return true;
    } catch (e) {
      toast.error('Export failed', { description: (e as Error).message });
      return false;
    }
  }
  async function createShare() {
    setShare({ url: null, busy: true });
    try {
      const r = await api<{ url: string }>(`/api/creators/${c.igPk}/share`, { method: 'POST', json: { window: wKey } });
      try { await navigator.clipboard.writeText(r.url); toast.success('Share link copied', { description: r.url }); } catch { /* shown below */ }
      setShare({ url: r.url, busy: false });
    } catch (e) {
      toast.error('Could not create a share link', { description: (e as Error).message });
      setShare(null);
    }
  }
  async function toggleSave() {
    try {
      await api(`/api/library/${c.igPk}`, { method: data!.saved ? 'DELETE' : 'POST' });
      setData({ ...data!, saved: !data!.saved });
      reloadLibrary();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }
  const presetBtn = (id: Preset, label: React.ReactNode) => (
    <button onClick={() => setPreset(id)} className={cn('inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold transition-colors', preset === id ? 'bg-primary text-primary-foreground shadow-sm' : 'border border-border bg-card hover:bg-secondary')}>{label}</button>
  );

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-16">
      {/* A. Action row */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link to="/feed" className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" />Back to Your Feed</Link>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" onClick={toggleSave}>{data.saved ? <><Check className="h-3.5 w-3.5 text-emerald-500" />In Your Feed</> : 'Save to Your Feed'}</Button>
          {data.reels.length > 0 && <Button variant="secondary" onClick={createShare} loading={share?.busy} title="Create a public, read-only link to this report">{share?.url ? <><Check className="h-3.5 w-3.5 text-emerald-500" />Link copied</> : <><Share2 className="h-3.5 w-3.5" />Share report</>}</Button>}
          <a href={igReelsTab(c.handle ?? handle)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-xl bg-primary/10 px-3.5 py-2 text-xs font-semibold text-primary hover:bg-primary/15">Instagram Profile<ExternalLink className="h-3.5 w-3.5" /></a>
        </div>
      </div>
      {share?.url && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-card p-3 text-xs">
          <Link2 className="h-4 w-4 text-primary" /><span className="text-muted-foreground">Public link for {windowLabel(win)} (transcripts and hooks need sign-in):</span>
          <input readOnly value={share.url} onFocus={(e) => e.currentTarget.select()} className="min-w-[220px] flex-1 rounded-lg border border-border bg-background px-2 py-1 font-mono" aria-label="Share link" />
        </div>
      )}

      {/* B. Profile header card. The status slot says "Syncing… 120/300 reels · 40 videos saving" while a scan is still
          being written (decision 75), else how many reels await verification. */}
      <ProfileHeader c={c} line={<>{c.isStub ? collabProfileNote(c.handle ?? handle) : <>Last scanned {ago(c.lastScannedAt)}</>}{syncLine(sync) ? ` · ${syncLine(sync)}` : data.awaitingVerification > 0 && ` · ${data.awaitingVerification} reel${data.awaitingVerification === 1 ? '' : 's'} awaiting verification`}</>} stats={
        <div className="grid shrink-0 grid-cols-3 divide-x divide-border rounded-2xl bg-secondary text-center">
          <div className="px-4 py-3" title={c.baselineValue ? undefined : notEnough}><div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground" title={`Median ${c.scoreBasis === 'likes' ? 'likes' : 'views'} of their recent reels; the Wise Score divides by this`}>{usual}</div><div className="text-lg font-black tabular-nums">{c.baselineValue ? compact(c.baselineValue) : '—'}</div></div>
          <div className="px-4 py-3"><div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Peak Wise Score</div><div className="text-lg font-black tabular-nums text-amber-600 dark:text-amber-400">{wiseScoreLabel(peak || null, data.tiers)}</div></div>
          <div className="px-4 py-3"><div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground" title={n.collabs ? `Their own reels, +${n.collabs} collab${n.collabs === 1 ? '' : 's'} listed too` : 'Their own reels in this window'}>Reels analysed</div><div className="text-lg font-black tabular-nums text-primary">{data.kpis.reels}</div></div>
        </div>
      } />

      {/* Tabs + window */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Segmented value={tab} onChange={(t) => setParam('tab', t === 'reels' ? null : t)} options={[
          { value: 'reels', label: <><Grid3x3 className="h-3.5 w-3.5" />Reels</> }, { value: 'report', label: <><BarChart3 className="h-3.5 w-3.5" />Report</> },
        ]} />
        <label className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">Window
          <Select value={wKey} onChange={(e) => setParam('window', e.target.value === 'all' ? null : e.target.value)}>
            {/* Decision 84: all reels by default (the list still renders PAGE at a time, "Load more"). */}
            <option value="all">{windowLabel({ kind: 'all' }, c.reelCount ?? null)}</option>
            {REPORT_LATEST_CHOICES.map((n) => <option key={n} value={`latest:${n}`}>Latest {n} reels</option>)}
            {REPORT_DAY_CHOICES.map((d) => <option key={d} value={`days:${d}`}>Last {d} days</option>)}
          </Select>
        </label>
      </div>

      {tab === 'report' ? (
        <ReportTab reels={data.reels} kpis={data.kpis} baseline={c.baselineValue} baselineN={c.baselineN} scoreWindow={data.scoreWindow ?? 0} threshold={data.outlierThreshold} notEnough={notEnough} onPick={(pk) => setOpenIdx(data.reels.findIndex((r) => r.pk === pk))} />
      ) : (
        <>
          {/* C. Toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              {presetBtn('all', `All Reels (${counts.all})`)}
              {presetBtn('top20', <><Sparkles className="h-3.5 w-3.5 text-amber-500" />Top 20 by Wise Score</>)}
              {presetBtn('mega', <><Flame className="h-3.5 w-3.5 text-red-500" />Viral and up (≥{formatWiseScore(data.tiers.viral)})</>)}
              {presetBtn('engagement', <><Zap className="h-3.5 w-3.5 text-yellow-500" />High engagement</>)}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {selected.size > 0 && <AddToCollection reelPks={[...selected]} label={`Add ${selected.size} to collection`} onDone={() => setSelected(new Set())} align="right" />}
              <Button onClick={() => setTxDialog({ exportFormat: null })} disabled={!!batch.state?.running}>
                {batch.state?.running ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Transcribing {batch.state.done + batch.state.failed}/{batch.state.total}</> : <><Mic className="h-3.5 w-3.5" />Transcribe all</>}
              </Button>
              <ExportMenu disabled={!visible.length} busy={!!batch.state?.running} onExport={(f) => doExport(f, false)} onTranscribeExport={(f) => setTxDialog({ exportFormat: f })} />
            </div>
          </div>

          {batch.state && <BatchProgress s={batch.state} onCancel={batch.cancel} onDismiss={batch.dismiss} />}

          {/* F. Search + sort + threshold */}
          <div className="flex flex-col gap-2 rounded-2xl bg-secondary/40 p-3 sm:flex-row sm:items-center">
            <Input icon={<Search className="h-4 w-4" />} className="sm:w-80" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search hook, caption or reel code…" aria-label="Search reels" />
            <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground"><Filter className="h-3.5 w-3.5" />At least
                <Select value={String(minMult)} onChange={(e) => setMinMult(Number(e.target.value))} aria-label="Minimum Wise Score">
                  <option value="0">any score</option>
                  {[data.tiers.above, data.tiers.outlier, data.tiers.viral, data.tiers.legendary].map((t) => <option key={t} value={t}>{formatWiseScore(t)}</option>)}
                </Select>
              </label>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">Sort by
                <Select value={sort} onChange={(e) => setSort(e.target.value as Sort)} disabled={preset === 'top20'}>{SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}</Select>
              </label>
            </div>
          </div>

          {/* H. Storage strip */}
          {data.reels.length > 0 && (
            <p className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-border px-3 py-2 text-xs text-muted-foreground">
              {data.saveVideos === false ? (
                // Decision 81: audio only by default; reels play in Instagram's embed.
                <span>🎧 Reels play from Instagram; we keep each reel's audio for transcription, not its video. Transcripts and stats stay.</span>
              ) : <span>🕒 Videos are kept for {data.videoRetentionDays} days after they're saved; transcripts and stats stay.</span>}
              {(data.saveVideos !== false || saved > 0) && <span className="font-semibold text-foreground">{saved} {data.saveVideos === false ? `saved video${saved === 1 ? '' : 's'}` : 'saved'}</span>}
              {expiring > 0 && <span className="font-semibold text-amber-600">{expiring} expiring within 3 days</span>}
              {data.saveVideos !== false && data.reels.length - saved > 0 && <span>{data.reels.length - saved} not saved (a scan saves every scanned reel's video)</span>}
            </p>
          )}

          {/* G/I. Grid or empty */}
          {!data.reels.length ? (
            <EmptyState icon={<Film className="h-7 w-7" />} title={`No reels indexed yet for @${c.handle}`}
              actions={<a className="text-sm font-semibold text-primary" href={igReelsTab(c.handle ?? handle)} target="_blank" rel="noreferrer">Open @{c.handle} on Instagram →</a>}>
              Open their Reels tab in Chrome and press Scan in the extension's side panel.
            </EmptyState>
          ) : !visible.length ? (
            <EmptyState icon={<Filter className="h-7 w-7" />} title="No reels match your current filter" actions={<Button variant="secondary" onClick={() => { setPreset('all'); setQ(''); setMinMult(0); }}>Reset filters</Button>}>
              Try clearing the search or switching to All Reels.
            </EmptyState>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {visible.slice(0, shown).map((r, i) => (
                  <ReelCard key={r.pk} r={r} tiers={data.tiers} handle={c.handle ?? handle} bookmarked={marks.has(r.pk)} onOpen={() => setOpenIdx(i)}
                    selected={selected.has(r.pk)} selecting={selected.size > 0} onSelect={() => toggleSel(r.pk)} />
                ))}
              </div>
              {visible.length > shown && <div className="flex justify-center"><Button variant="secondary" onClick={() => setShown(shown + PAGE)}>Load more ({visible.length - shown} left)</Button></div>}
            </>
          )}
        </>
      )}

      {openIdx !== null && openIdx >= 0 && (tab === 'report' ? data.reels : visible)[openIdx] && (
        <ReelInspector reels={tab === 'report' ? data.reels : visible} index={openIdx} onIndex={setOpenIdx} onClose={() => setOpenIdx(null)} handle={c.handle ?? handle}
          tiers={data.tiers} secondsPerCredit={data.secondsPerCredit} retentionDays={data.videoRetentionDays} saveVideos={data.saveVideos} bookmarked={marks} scoreBasis={c.scoreBasis} notEnough={notEnough} noScore={stubNote}
          onBookmark={(pk, on) => setMarks((m) => { const n = new Set(m); on ? n.add(pk) : n.delete(pk); return n; })}
          onTranscribed={(pk, t) => setData((d) => d && ({ ...d, reels: d.reels.map((x) => (x.pk === pk ? { ...x, hasTranscript: true, hook: t.hook ?? x.hook } : x)) }))} />
      )}

      <TranscribeAllDialog open={!!txDialog} onClose={() => setTxDialog(null)} reels={visible} handle={c.handle ?? handle} secondsPerCredit={data.secondsPerCredit}
        exportLabel={txDialog?.exportFormat ? FORMAT_LABEL[txDialog.exportFormat] : null}
        onExportOnly={() => txDialog?.exportFormat && doExport(txDialog.exportFormat, true)}
        onStart={(plan) => {
          const fmt = txDialog?.exportFormat ?? null;
          const rows = visible; // the rows the user chose to export, in the order they saw
          void batch.run(plan, fmt ? FORMAT_LABEL[fmt] : null).then(async (finished) => {
            if (fmt && (await doExport(fmt, true, rows, finished))) batch.exported();
          });
        }} />
    </div>
  );
}
